import { useEffect, useState } from "react";
import { Alert, Badge, Card, Col, Row, Table } from "react-bootstrap";
import PageTitle from "@/components/PageTitle";
import FullScreenLoader from "@/components/loaders/screenLoader";
import {
  fetchGnnHealth,
  fetchGnnRollout,
  fetchGnnStatus,
} from "@/services/api/gnn.api";
import type {
  GnnHealthResponse,
  GnnRolloutResponse,
  GnnStatusResponse,
} from "@/types/gnn";

const GnnStatusPage = () => {
  const [status, setStatus] = useState<GnnStatusResponse | null>(null);
  const [health, setHealth] = useState<GnnHealthResponse | null>(null);
  const [rollout, setRollout] = useState<GnnRolloutResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [statusResponse, healthResponse, rolloutResponse] =
          await Promise.all([
            fetchGnnStatus(),
            fetchGnnHealth(),
            fetchGnnRollout(),
          ]);
        if (!mounted) return;
        setStatus(statusResponse);
        setHealth(healthResponse);
        setRollout(rolloutResponse);
      } catch {
        if (!mounted) return;
        setError("Unable to load GNN operational status.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      <PageTitle title="GNN Operations" />

      {loading ? <FullScreenLoader /> : null}

      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {!loading && !error && status && health && rollout ? (
        <>
          <Row className="g-3 mb-4">
            <Col xs={12} md={4}>
              <Card className="h-100">
                <Card.Header>Rollout</Card.Header>
                <Card.Body>
                  <div className="pz-kv"><span className="pz-kv__label">Effective stage</span><span className="pz-kv__value"><Badge bg="primary">{status.effectiveStage}</Badge></span></div>
                  <div className="pz-kv"><span className="pz-kv__label">Override</span><span className="pz-kv__value">{status.overrideStage || "none"}</span></div>
                  <div className="pz-kv"><span className="pz-kv__label">Canary %</span><span className="pz-kv__value">{rollout.canaryPercent}%</span></div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card className="h-100">
                <Card.Header>Readiness</Card.Header>
                <Card.Body>
                  <div className="pz-kv"><span className="pz-kv__label">Readiness stage</span><span className="pz-kv__value">{rollout.readinessStage || "off"}</span></div>
                  <div className="pz-kv"><span className="pz-kv__label">Tier</span><span className="pz-kv__value">{status.readiness?.tier || "unknown"}</span></div>
                  <div className="pz-kv"><span className="pz-kv__label">Model gate passed</span><span className="pz-kv__value">{status.readiness?.model_gate_passed === null ||
                      status.readiness?.model_gate_passed === undefined
                        ? "n/a"
                        : status.readiness?.model_gate_passed
                          ? "yes"
                          : "no"}</span></div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card className="h-100">
                <Card.Header>Downstream health</Card.Header>
                <Card.Body>
                  <div className="pz-kv"><span className="pz-kv__label">GNN API</span><span className="pz-kv__value"><Badge bg={health.gnnHealth.healthy ? "success" : "danger"}>
                      {health.gnnHealth.healthy ? "✓ healthy" : "✕ unhealthy"}
                    </Badge></span></div>
                  <div className="small text-muted mt-2 mb-1">{health.gnnHealth.message}</div>
                  <div className="small text-muted">
                    Readiness last fetch: {health.readinessLastFetchedAt || "n/a"}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card>
            <Card.Header>Latency and fallback metrics</Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Metric</th>
                    <th className="text-end">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Total requests</td>
                    <td className="text-end pz-mono">{status.metrics.totalRequests}</td>
                  </tr>
                  <tr>
                    <td>GNN requests</td>
                    <td className="text-end pz-mono">{status.metrics.gnnRequests}</td>
                  </tr>
                  <tr>
                    <td>Legacy requests</td>
                    <td className="text-end pz-mono">{status.metrics.legacyRequests}</td>
                  </tr>
                  <tr>
                    <td>Fallback count</td>
                    <td className="text-end pz-mono">{status.metrics.fallbackCount}</td>
                  </tr>
                  <tr>
                    <td>Fallback rate</td>
                    <td className="text-end pz-mono">{status.metrics.fallbackRatePct}%</td>
                  </tr>
                  <tr>
                    <td>GNN avg / p95 latency (ms)</td>
                    <td className="text-end">
                      {status.metrics.avgGnnLatencyMs} / {status.metrics.p95GnnLatencyMs}
                    </td>
                  </tr>
                  <tr>
                    <td>Legacy avg / p95 latency (ms)</td>
                    <td className="text-end">
                      {status.metrics.avgLegacyLatencyMs} / {status.metrics.p95LegacyLatencyMs}
                    </td>
                  </tr>
                  <tr>
                    <td>Last fallback reason</td>
                    <td className="text-end pz-mono">{status.metrics.lastFallbackReason || "none"}</td>
                  </tr>
                  <tr>
                    <td>Last fallback at</td>
                    <td className="text-end pz-mono">{status.metrics.lastFallbackAt || "n/a"}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      ) : null}
    </>
  );
};

export default GnnStatusPage;
